import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiTrash2, FiPlus, FiMinus, FiClock, FiImage, FiVideo, FiType } = FiIcons;

const NodeEditor = ({ node, onClose, onUpdate, onDelete, allNodes }) => {
  const [formData, setFormData] = useState({
    label: '',
    type: 'multiple-choice',
    questionText: '',
    choices: [],
    correctAnswer: null,
    timer: null,
    imageUrl: '',
    videoUrl: '',
    isSuccess: true,
    // Short answer specific fields
    correctAnswers: [''],
    ignoreCase: false,
    correctNextNodeId: null,
    incorrectNextNodeId: null,
    ...node.data
  });

  useEffect(() => {
    setFormData({
      label: '',
      type: 'multiple-choice',
      questionText: '',
      choices: [],
      correctAnswer: null,
      timer: null,
      imageUrl: '',
      videoUrl: '',
      isSuccess: true,
      // Short answer specific fields
      correctAnswers: [''],
      ignoreCase: false,
      correctNextNodeId: null,
      incorrectNextNodeId: null,
      ...node.data
    });
  }, [node]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(formData);
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addChoice = () => {
    const newChoice = {
      id: Date.now(),
      text: '',
      isCorrect: false,
      nextNodeId: null
    };
    setFormData(prev => ({
      ...prev,
      choices: [...(prev.choices || []), newChoice]
    }));
  };

  const removeChoice = (choiceId) => {
    setFormData(prev => ({
      ...prev,
      choices: prev.choices.filter(choice => choice.id !== choiceId)
    }));
  };

  const updateChoice = (choiceId, field, value) => {
    setFormData(prev => ({
      ...prev,
      choices: prev.choices.map(choice =>
        choice.id === choiceId
          ? { ...choice, [field]: value }
          : choice
      )
    }));
  };

  const setCorrectChoice = (choiceId) => {
    setFormData(prev => ({
      ...prev,
      choices: prev.choices.map(choice => ({
        ...choice,
        isCorrect: choice.id === choiceId
      }))
    }));
  };

  // Short answer functions
  const addCorrectAnswer = () => {
    if (formData.correctAnswers.length < 20) {
      setFormData(prev => ({
        ...prev,
        correctAnswers: [...prev.correctAnswers, '']
      }));
    }
  };

  const removeCorrectAnswer = (index) => {
    if (formData.correctAnswers.length > 1) {
      setFormData(prev => ({
        ...prev,
        correctAnswers: prev.correctAnswers.filter((_, i) => i !== index)
      }));
    }
  };

  const updateCorrectAnswer = (index, value) => {
    setFormData(prev => ({
      ...prev,
      correctAnswers: prev.correctAnswers.map((answer, i) => 
        i === index ? value : answer
      )
    }));
  };

  const availableNodes = allNodes.filter(n => n.id !== node.id);

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold font-rubik" style={{ color: '#2b6777' }}>
              Knoten bearbeiten: {formData.label}
            </h2>
            <div className="flex items-center space-x-2">
              {node.id !== 'start' && (
                <button
                  onClick={() => {
                    if (confirm('Sind Sie sicher, dass Sie diesen Knoten löschen möchten?')) {
                      onDelete();
                    }
                  }}
                  className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
                  Knotenbeschriftung
                </label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => handleChange('label', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                  style={{ color: '#2b6777' }}
                  placeholder="Knotenbeschriftung eingeben"
                />
              </div>

              {node.id !== 'start' && (
                <div>
                  <label className="block text-sm font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
                    Knotentyp
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                    style={{ color: '#2b6777' }}
                  >
                    <option value="multiple-choice">Multiple Choice</option>
                    <option value="true-false">Richtig/Falsch</option>
                    <option value="short-answer">Kurzantwort</option>
                    <option value="end">Endknoten</option>
                  </select>
                </div>
              )}
            </div>

            {/* End Node Settings */}
            {formData.type === 'end' && (
              <div>
                <label className="block text-sm font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
                  Endtyp
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center font-rubik" style={{ color: '#2b6777' }}>
                    <input
                      type="radio"
                      checked={formData.isSuccess === true}
                      onChange={() => handleChange('isSuccess', true)}
                      className="mr-2"
                    />
                    Erfolg (Zertifikat)
                  </label>
                  <label className="flex items-center font-rubik" style={{ color: '#2b6777' }}>
                    <input
                      type="radio"
                      checked={formData.isSuccess === false}
                      onChange={() => handleChange('isSuccess', false)}
                      className="mr-2"
                    />
                    Spiel beendet
                  </label>
                </div>
              </div>
            )}

            {/* Question Settings */}
            {(formData.type === 'multiple-choice' || formData.type === 'true-false' || formData.type === 'short-answer') && (
              <>
                {/* Question Text */}
                <div>
                  <label className="block text-sm font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
                    Fragetext
                  </label>
                  <textarea
                    value={formData.questionText}
                    onChange={(e) => handleChange('questionText', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                    style={{ color: '#2b6777' }}
                    placeholder="Geben Sie Ihre Frage hier ein..."
                  />
                </div>

                {/* Media */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
                      <SafeIcon icon={FiImage} className="inline w-4 h-4 mr-1" />
                      Bild-URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={formData.imageUrl}
                      onChange={(e) => handleChange('imageUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                      style={{ color: '#2b6777' }}
                      placeholder="https://example.com/bild.jpg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
                      <SafeIcon icon={FiVideo} className="inline w-4 h-4 mr-1" />
                      YouTube-URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={formData.videoUrl}
                      onChange={(e) => handleChange('videoUrl', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                      style={{ color: '#2b6777' }}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                </div>

                {/* Timer */}
                <div>
                  <label className="block text-sm font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
                    <SafeIcon icon={FiClock} className="inline w-4 h-4 mr-1" />
                    Timer (Sekunden, optional)
                  </label>
                  <input
                    type="number"
                    min="10"
                    max="300"
                    value={formData.timer || ''}
                    onChange={(e) => handleChange('timer', e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                    style={{ color: '#2b6777' }}
                    placeholder="Leer lassen für keinen Timer"
                  />
                </div>

                {/* Short Answer Options */}
                {formData.type === 'short-answer' && (
                  <div>
                    <div className="mb-6">
                      <div className="flex justify-between items-center mb-4">
                        <label className="block text-sm font-medium font-rubik" style={{ color: '#2b6777' }}>
                          <SafeIcon icon={FiType} className="inline w-4 h-4 mr-1" />
                          Richtige Antworten
                        </label>
                        <button
                          type="button"
                          onClick={addCorrectAnswer}
                          disabled={formData.correctAnswers.length >= 20}
                          className="flex items-center space-x-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-rubik"
                          style={{ color: '#2b6777' }}
                        >
                          <SafeIcon icon={FiPlus} className="w-4 h-4" />
                          <span>Richtige Antwort hinzufügen</span>
                        </button>
                      </div>

                      <div className="space-y-3">
                        {formData.correctAnswers.map((answer, index) => (
                          <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded">
                            <input
                              type="text"
                              value={answer}
                              onChange={(e) => updateCorrectAnswer(index, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                              style={{ color: '#2b6777' }}
                              placeholder={`Richtige Antwort ${index + 1}`}
                              maxLength={255}
                            />
                            {formData.correctAnswers.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeCorrectAnswer(index)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                              >
                                <SafeIcon icon={FiMinus} className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Case sensitivity option */}
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.ignoreCase}
                            onChange={(e) => handleChange('ignoreCase', e.target.checked)}
                            className="mr-3"
                          />
                          <span className="text-sm font-rubik" style={{ color: '#2b6777' }}>
                            Groß-/Kleinschreibung ignorieren
                          </span>
                        </label>
                        <p className="text-xs mt-1 font-rubik" style={{ color: '#6b7280' }}>
                          {formData.ignoreCase 
                            ? 'Schülerantworten werden ohne Berücksichtigung der Groß-/Kleinschreibung verglichen'
                            : 'Schülerantworten müssen exakt übereinstimmen, einschließlich Groß-/Kleinschreibung'
                          }
                        </p>
                      </div>

                      {/* Navigation paths for short answer */}
                      <div className="mt-6 space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
                            Richtige Antwort führt zu:
                          </label>
                          <select
                            value={formData.correctNextNodeId || ''}
                            onChange={(e) => handleChange('correctNextNodeId', e.target.value || null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                            style={{ color: '#2b6777' }}
                          >
                            <option value="">Nächsten Knoten auswählen</option>
                            {availableNodes.map(n => (
                              <option key={n.id} value={n.id}>
                                {n.data.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2 font-rubik" style={{ color: '#2b6777' }}>
                            Falsche Antwort führt zu:
                          </label>
                          <select
                            value={formData.incorrectNextNodeId || ''}
                            onChange={(e) => handleChange('incorrectNextNodeId', e.target.value || null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                            style={{ color: '#2b6777' }}
                          >
                            <option value="">Nächsten Knoten auswählen</option>
                            {availableNodes.map(n => (
                              <option key={n.id} value={n.id}>
                                {n.data.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Multiple Choice Options */}
                {formData.type === 'multiple-choice' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <label className="block text-sm font-medium font-rubik" style={{ color: '#2b6777' }}>
                        Antwortmöglichkeiten
                      </label>
                      <button
                        type="button"
                        onClick={addChoice}
                        className="flex items-center space-x-1 transition-colors font-rubik"
                        style={{ color: '#2b6777' }}
                      >
                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                        <span>Option hinzufügen</span>
                      </button>
                    </div>

                    <div className="space-y-3">
                      {(formData.choices || []).map((choice, index) => (
                        <div key={choice.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              checked={choice.isCorrect}
                              onChange={() => setCorrectChoice(choice.id)}
                              className="mr-2"
                            />
                            <span className="text-sm font-rubik" style={{ color: '#2b6777' }}>Richtig</span>
                          </div>
                          
                          <input
                            type="text"
                            value={choice.text}
                            onChange={(e) => updateChoice(choice.id, 'text', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                            style={{ color: '#2b6777' }}
                            placeholder={`Option ${index + 1}`}
                          />

                          <select
                            value={choice.nextNodeId || ''}
                            onChange={(e) => updateChoice(choice.id, 'nextNodeId', e.target.value || null)}
                            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                            style={{ color: '#2b6777' }}
                          >
                            <option value="">Nächsten Knoten auswählen</option>
                            {availableNodes.map(n => (
                              <option key={n.id} value={n.id}>
                                {n.data.label}
                              </option>
                            ))}
                          </select>

                          {formData.choices.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeChoice(choice.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <SafeIcon icon={FiMinus} className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {(!formData.choices || formData.choices.length < 2) && (
                      <button
                        type="button"
                        onClick={addChoice}
                        className="w-full py-3 border-2 border-dashed border-gray-300 rounded text-gray-500 hover:border-gray-400 transition-colors font-rubik"
                      >
                        Mindestens 2 Optionen hinzufügen
                      </button>
                    )}
                  </div>
                )}

                {/* True/False Options */}
                {formData.type === 'true-false' && (
                  <div>
                    <label className="block text-sm font-medium mb-4 font-rubik" style={{ color: '#2b6777' }}>
                      Antwortoptionen
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            checked={formData.correctAnswer === true}
                            onChange={() => handleChange('correctAnswer', true)}
                            className="mr-2"
                          />
                          <span className="text-sm font-rubik" style={{ color: '#2b6777' }}>Richtig</span>
                        </div>
                        <span className="flex-1 font-medium font-rubik" style={{ color: '#2b6777' }}>Wahr</span>
                        <select
                          value={formData.trueNextNodeId || ''}
                          onChange={(e) => handleChange('trueNextNodeId', e.target.value || null)}
                          className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                          style={{ color: '#2b6777' }}
                        >
                          <option value="">Nächsten Knoten auswählen</option>
                          {availableNodes.map(n => (
                            <option key={n.id} value={n.id}>
                              {n.data.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            checked={formData.correctAnswer === false}
                            onChange={() => handleChange('correctAnswer', false)}
                            className="mr-2"
                          />
                          <span className="text-sm font-rubik" style={{ color: '#2b6777' }}>Richtig</span>
                        </div>
                        <span className="flex-1 font-medium font-rubik" style={{ color: '#2b6777' }}>Falsch</span>
                        <select
                          value={formData.falseNextNodeId || ''}
                          onChange={(e) => handleChange('falseNextNodeId', e.target.value || null)}
                          className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 font-rubik"
                          style={{ color: '#2b6777' }}
                        >
                          <option value="">Nächsten Knoten auswählen</option>
                          {availableNodes.map(n => (
                            <option key={n.id} value={n.id}>
                              {n.data.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 transition-colors font-rubik"
                style={{ color: '#2b6777' }}
              >
                Abbrechen
              </button>
              <motion.button
                type="submit"
                className="px-6 py-2 rounded transition-colors text-white font-rubik"
                style={{ backgroundColor: '#c8d8e4' }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Änderungen speichern
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NodeEditor;